// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Box } from 'grommet'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'lodash'
import { useNavigate } from 'react-router-dom'

import {
  Typography,
  Button,
  ModalDialog,
  Notification,
  CCSForm,
  FormInput,
  ModalHeader
} from '../../../../components'
import { del } from '../../../../utils/api-utils'
import { displayApiError } from '../../../../utils/error-handling-utils'

const DeleteScimIntegerationModal = ({
  setShowDeleteModal,
  scimId,
  scimName
}) => {
  const { t } = useTranslation(['authz', 'common', 'iam'])
  const navigate = useNavigate()
  const [confirmDeleteText, setConfirmDeleteText] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [apiErrorNotification, setApiErrorNotification] = useState(null)
  const [loading, setLoading] = useState(false)

  const handleOnDeleteScim = () => {
    const confirmText = confirmDeleteText?.trim()
    if (isEmpty(confirmText)) setErrorMsg(t('common:this_is_required'))
    else if (confirmText && confirmText !== t('common:delete_keyword'))
      setErrorMsg(t('common:invalid_keyword'))
    else {
      setLoading(true)
      del(`/internal-identity/v2alpha1/scim-integrations/${scimId}`).then(
        () => {
          setLoading(false)
          navigate(`/manage-account/organization/external-scim`, {
            state: {
              notificationMessage: `External SCIM integration ${scimName} has been deleted.`
              // t('roles.role_create_notification_msg', {
              //   roleName: formValues?.roleDetails?.name
              // })
            }
          })
        },
        (error) => {
          setLoading(false)
          setApiErrorNotification(
            displayApiError(error, t, setApiErrorNotification)
          )
        }
      )
    }
  }

  return (
    <>
      <ModalDialog
        overflow="hidden"
        position="right"
        height="100%"
        testId="confirm-delete-scim-integration-modal-dialog"
        header={
          <ModalHeader
            title={
              <Typography
                type="heading"
                level="2"
                testId="delete-scim-integration-heading"
              >
                {t('iam:external_scim_integrations.delete_credential')}
              </Typography>
            }
          />
        }
        content={
          <Box gap="xsmall" width="medium">
            {apiErrorNotification}
            <CCSForm
              testId="delete-scim-integration-modal-form"
              errorMessage=""
              onSubmit={handleOnDeleteScim}
              buttons={
                <Box direction="row" margin={{ top: 'medium' }}>
                  <Button
                    label={t(
                      'iam:external_scim_integrations.delete_credential'
                    )}
                    primary
                    type="submit"
                    testId="delete-scim-integration-btn"
                    disabled={loading}
                  />
                  <Button
                    label={t('common:cancel')}
                    onClick={() => setShowDeleteModal(false)}
                    disabled={loading}
                    testId="cancel-button"
                  />
                </Box>
              }
            >
              <>
                <Typography type="text" margin={{ top: 'small' }} size="medium">
                  {t('iam:external_scim_integrations.delete_credential_desc')}
                </Typography>

                <Notification
                  margin="small"
                  type="inline"
                  text={t(
                    'iam:external_scim_integrations.delete_credential_notification_1'
                  )}
                  status="warning"
                  testId="delete-root-wkspc-warning"
                />
                <Notification
                  margin="small"
                  type="inline"
                  text={t(
                    'iam:external_scim_integrations.delete_credential_notification_2'
                  )}
                  status="warning"
                  testId="delete-root-wkspc-warning-2"
                />
                <FormInput
                  required
                  name="inputText"
                  inputType="text"
                  label={t('common:confirmation')}
                  labelHelper={
                    <Typography type="text" size="xsmall">
                      {t(
                        'iam:external_scim_integrations.delete_credential_confirm_message'
                      )}
                    </Typography>
                  }
                  testId="delete-account"
                  error={errorMsg}
                  value={confirmDeleteText}
                  onChange={(event) => {
                    setConfirmDeleteText(event.target.value)
                    setErrorMsg('')
                  }}
                />
              </>
            </CCSForm>
          </Box>
        }
        onClose={() => setShowDeleteModal(false)}
      />
    </>
  )
}

DeleteScimIntegerationModal.propTypes = {
  setShowDeleteModal: PropTypes.func.isRequired,
  scimId: PropTypes.string.isRequired,
  scimName: PropTypes.string.isRequired
}

export default DeleteScimIntegerationModal
